import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlatformInfo } from '../pages/CatalogPage';
import { Smartphone, Tablet, Monitor, Globe } from 'lucide-react';
import PlatformCard from './PlatformCard';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { HeroPattern } from './HeroPattern';
import { getDisplayName } from '../utils/platformMapping';

type HomePageProps = {
  platforms: PlatformInfo[];
  onCountryClick?: (countryCode: string, countryName: string) => void;
  loading: boolean;
  theme?: 'light' | 'dark';
}

type PlatformCategorizationStrategy = (platformName: string) => string;

type HardwareIcon = { icon: React.ComponentType<{ className?: string }>; rotate?: boolean };

// ─── Categorization ────────────────────────────────────────────────────────────

const INTEGRATION_KEYWORDS = ['3pi', 'partner integrations', 'integrated payments', 'marketplace integrations'];

const categorizationRules: { matches: (n: string) => boolean; section: string }[] = [
  { matches: n => INTEGRATION_KEYWORDS.some(kw => n.includes(kw)), section: 'Integrations' },
  { matches: n => n === 'shift4 payments', section: 'Shift4 Payments' },
];

const defaultCategorizationStrategy: PlatformCategorizationStrategy = platformName => {
  const name = platformName.toLowerCase();
  return categorizationRules.find(r => r.matches(name))?.section ?? 'Products';
};

// ─── Platform image ────────────────────────────────────────────────────────────

const DEFAULT_PLATFORM_IMAGE = 'GlobalProductCatalog_Mobile.svg';

const platformImageRules: { matches: (n: string) => boolean; image: string }[] = [
  { matches: n => n.includes('shift4 dine'), image: 'Shift4Dine.svg' },
  { matches: n => n.includes('shift4 venue'), image: 'Shift4Venue.svg' },
  { matches: n => n.includes('shift4 one'), image: 'Shift4One.svg' },
  { matches: n => n.includes('shift4 duo'), image: 'Shift4Duo.svg' },
  { matches: n => n === 'vas', image: 'ValueAddedServices.svg' },
  { matches: n => n === 'ecommerce', image: 'eCommerce.svg' },
  { matches: n => n === 'payment methods', image: 'Payments.svg' },
  { matches: n => n === 'tax free shopping', image: 'TaxFreeShopping.svg' },
  { matches: n => n === 'legacy', image: 'Legacy.svg' },
  { matches: n => n.includes('customer hub'), image: 'CustomerHub.svg' },
  { matches: n => n.includes('business manager'), image: 'GlobalProductCatalog_LBM.svg' },
  { matches: n => n.includes('partner portal'), image: 'PortalsAndData.svg' },
  { matches: n => ['3pi', 'integrated payments', 'partner integrations', 'marketplace integrations'].some(kw => n.includes(kw)), image: 'IntegratedPayments.svg' },
  { matches: n => n === 'shift4 payments', image: 'GlobalProductCatalog_Payments.svg' },
];

const getPlatformImage = (platformName: string): string => {
  const name = platformName.trim().toLowerCase();
  return platformImageRules.find(r => r.matches(name))?.image ?? DEFAULT_PLATFORM_IMAGE;
};

// ─── Platform image size ───────────────────────────────────────────────────────

const DEFAULT_IMAGE_SIZE = 'w-[100%] h-[100%]';

const platformImageSizeRules: { matches: (n: string) => boolean; size: string }[] = [
  { matches: n => n.includes('shift4 dine'), size: 'w-[115%] h-[115%] opacity-100' },
  { matches: n => n.includes('shift4 venue'), size: 'w-[110%] h-[110%] opacity-100' },
  { matches: n => n.includes('shift4 one'), size: 'w-[100%] h-[100%] opacity-100' },
  { matches: n => n.includes('shift4 duo'), size: 'w-[100%] h-[100%]' },
  { matches: n => n.includes('business manager') || n.includes('customer hub'), size: 'w-[115%] h-[115%]' },
  { matches: n => ['3pi', 'integrated payments', 'partner integrations'].some(kw => n.includes(kw)), size: 'w-[100%] h-[100%]' },
  { matches: n => n === 'shift4 payments', size: 'w-[110%] h-[110%]' },
];

const getPlatformImageSize = (platformName: string): string => {
  const name = platformName.trim().toLowerCase();
  return platformImageSizeRules.find(r => r.matches(name))?.size ?? DEFAULT_IMAGE_SIZE;
};

// ─── Platform order ────────────────────────────────────────────────────────────

const platformOrderRules: { matches: (n: string) => boolean; order: number }[] = [
  { matches: n => n.includes('shift4 dine') || (n.includes('skytab') && !n.includes('venue')), order: 0 },
  { matches: n => n.includes('shift4 venue') || (n.includes('skytab') && n.includes('venue')), order: 1 },
  { matches: n => n.includes('shift4 one'), order: 2 },
  { matches: n => n.includes('shift4 duo') || n.includes('shift4 mobile'), order: 3 },
  { matches: n => n === 'vas', order: 4 },
  { matches: n => n === 'ecommerce', order: 5 },
  { matches: n => n === 'payment methods', order: 6 },
  { matches: n => n === 'tax free shopping', order: 7 },
  { matches: n => n.includes('business manager') || n.includes('customer hub') || n.includes('lbm'), order: 8 },
  { matches: n => n.includes('partner portal'), order: 9 },
  { matches: n => n === 'legacy', order: 10 },
  { matches: n => ['marketplace integrations', '3pi', 'integrated payments', 'partner integrations'].some(kw => n.includes(kw)), order: 2 },
  { matches: n => n === 'shift4 payments', order: 10 },
];

const getPlatformOrder = (platformName: string): number => {
  const name = platformName.trim().toLowerCase();
  return platformOrderRules.find(r => r.matches(name))?.order ?? 99;
};

// ─── Hardware icons ────────────────────────────────────────────────────────────

const DEFAULT_HARDWARE_ICONS: HardwareIcon[] = [{ icon: Globe }, { icon: Monitor }];

const hardwareIconRules: { matches: (n: string) => boolean; icons: HardwareIcon[] }[] = [
  { matches: n => n.includes('shift4 dine') || (n.includes('skytab') && !n.includes('venue')), icons: [{ icon: Smartphone }, { icon: Tablet, rotate: true }, { icon: Monitor }] },
  { matches: n => n.includes('shift4 venue') || (n.includes('skytab') && n.includes('venue')), icons: [{ icon: Smartphone }, { icon: Tablet, rotate: true }] },
  { matches: n => n.includes('shift4 one'), icons: [{ icon: Smartphone }] },
  { matches: n => n.includes('shift4 duo') || n.includes('shift4 mobile'), icons: [{ icon: Smartphone }] },
  { matches: n => n.includes('online') || n === 'shift4 payments', icons: [{ icon: Globe }, { icon: Monitor }] },
  { matches: n => n.includes('business manager') || n.includes('lbm') || n.includes('customer hub'), icons: [{ icon: Globe }, { icon: Monitor }] },
  { matches: n => ['3pi', 'partner integrations', 'integrated payments'].some(kw => n.includes(kw)), icons: [{ icon: Smartphone }, { icon: Tablet, rotate: true }, { icon: Monitor }] },
];

const getHardwareIcons = (platform: PlatformInfo): HardwareIcon[] => {
  const name = platform.name.trim().toLowerCase();
  return hardwareIconRules.find(r => r.matches(name))?.icons ?? DEFAULT_HARDWARE_ICONS;
};

const INTEGRATION_HUB_URL = 'https://atlas.shift4.engineering/partners';

type SectionConfig = {
  id: string;
  ref: React.RefObject<HTMLDivElement>;
  label: string;
  description: string;
  fromParam: string;
  platforms: PlatformInfo[];
  onPlatformClick?: (platform: PlatformInfo) => void;
};

type SectionProps = {
  section: SectionConfig;
  navigate: ReturnType<typeof useNavigate>;
  theme: 'light' | 'dark';
  className?: string;
  headerClassName?: string;
}

function Section({ section, navigate, theme, className, headerClassName }: SectionProps) {
  return (
    <section ref={section.ref} id={section.id} className={className}>
      <h2 className={`text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2 ${headerClassName ?? ''}`}>
        {section.label}
      </h2>
      {section.description && (
        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 mb-6">
          {section.description}
        </p>
      )}
      <div className="flex flex-wrap gap-4">
        {section.platforms.map(platform => (
          <PlatformCard
            key={`${section.id}-${platform.name}`}
            platform={{ ...platform, name: getDisplayName(platform.name) }}
            isAvailable={platform.isAvailable}
            platformImage={getPlatformImage(platform.name)}
            platformImageSize={getPlatformImageSize(platform.name)}
            hardwareIcons={getHardwareIcons(platform)}
            onClick={() =>
              section.onPlatformClick
                ? section.onPlatformClick(platform)
                : navigate(`/platform/${encodeURIComponent(platform.name)}?from=${section.fromParam}`)
            }
            theme={theme}
          />
        ))}
      </div>
    </section>
  );
}

type LoadingSpinnerProps = { theme: string };

function LoadingSpinner({ theme }: LoadingSpinnerProps) {
  return (
    <div className="flex items-center justify-center py-12 w-full">
      <div
        className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
          theme === 'light' ? 'border-gray-900' : 'border-white'
        }`}
      />
    </div>
  );
}

function HomePage({
  platforms,
  onCountryClick: _onCountryClick,
  loading,
  theme = 'dark',
}: HomePageProps) {
  const navigate = useNavigate();
  const { setActiveTab } = useProductMatrix();

  const categorizePlatform = defaultCategorizationStrategy;

  const productsPlatforms = platforms
    .filter(p => categorizePlatform(p.name) === 'Products')
    .sort((a, b) => getPlatformOrder(a.name) - getPlatformOrder(b.name));

  const integrationsPlatforms = platforms
    .filter(p => categorizePlatform(p.name) === 'Integrations')
    .sort((a, b) => getPlatformOrder(a.name) - getPlatformOrder(b.name));

  const productsRef = useRef<HTMLDivElement>(null);
  const integrationsRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingProgrammatically = useRef(false);

  const sections: SectionConfig[] = [
    {
      id: 'products-section',
      ref: productsRef,
      label: 'Products',
      description: 'Explore our suite of point-of-sale systems and business management solutions.',
      fromParam: 'Products',
      platforms: productsPlatforms,
    },
    {
      id: 'integrations-section',
      ref: integrationsRef,
      label: 'Integrations',
      description:
        'Explore our partner integrations and 3rd-party platforms that work seamlessly with Shift4 products.',
      fromParam: '3PI',
      platforms: integrationsPlatforms,
      onPlatformClick: (platform) => {
        if (platform.name === 'Marketplace Integrations') {
          window.open(INTEGRATION_HUB_URL, '_blank', 'noopener,noreferrer');
        } else {
          navigate(`/platform/${encodeURIComponent(platform.name)}?from=3PI`);
        }
      },
    },
  ];

  const scrollToSection = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section?.ref.current) {
      isScrollingProgrammatically.current = true;
      section.ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 600);
    }
  };

  useEffect(() => {
    if (window.location.hash === '#integrations') {
      setTimeout(() => scrollToSection('integrations-section'), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScrollRequest = ((event: CustomEvent) => {
      scrollToSection(event.detail.sectionId);
    }) as EventListener;
    window.addEventListener('scrollToSection', handleScrollRequest);
    return () => window.removeEventListener('scrollToSection', handleScrollRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return;
      let mostVisibleTabId = sections[0]?.id ?? '';
      let maxVisibility = 0;

      sections.forEach(({ ref, id }) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const visibleHeight = Math.max(
          0,
          Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top)
        );
        if (visibleHeight > maxVisibility) {
          maxVisibility = visibleHeight;
          mostVisibleTabId = id;
        }
      });

      if (maxVisibility > 0) setActiveTab(mostVisibleTabId);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setActiveTab]);

  return (
    <>
      <HeroPattern />

      <div className="absolute inset-0">
        <div
          className="absolute top-0 right-0 w-72 h-72 opacity-0"
          style={{
            backgroundImage:
              theme === 'light'
                ? 'radial-gradient(circle, #9CA3AF 2px, transparent 2px)'
                : 'radial-gradient(circle, #6B7280 2px, transparent 2px)',
            backgroundSize: '40px 40px',
            backgroundPosition: '25px 25px',
          }}
        />

        {/* Mobile */}
        <div ref={scrollContainerRef} className="lg:hidden relative z-10 h-full overflow-y-auto">
          <div className="min-h-full flex items-start justify-start pt-8 px-6 lg:px-8">
            <div className="flex flex-col items-start justify-start space-y-6 text-left py-4 w-full max-w-5xl mx-auto">
              {loading ? (
                <LoadingSpinner theme={theme} />
              ) : (
                sections.map(section => (
                  <Section
                    key={section.id}
                    section={section}
                    navigate={navigate}
                    theme={theme}
                    className="w-full scroll-mt-32"
                    headerClassName="mb-6"
                  />
                ))
              )}
              <div className="h-32 w-full" />
            </div>
          </div>
        </div>

        {/* Desktop */}
        <div
          ref={scrollContainerRef}
          className="hidden lg:block relative z-10 w-full h-screen overflow-y-auto px-6 lg:px-8"
        >
          <div className="max-w-5xl mx-auto text-left py-8 mt-20">
            {loading ? (
              <LoadingSpinner theme={theme} />
            ) : (
              sections.map(section => (
                <Section
                  key={section.id}
                  section={section}
                  navigate={navigate}
                  theme={theme}
                  className="scroll-mt-32 mb-16"
                />
              ))
            )}
            <div className="h-32 w-full" />
          </div>
        </div>
      </div>
    </>
  );
}

export default HomePage;
